/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
import { BlogPostPageStoryblok, RichtextStoryblok } from '@/lib/storyblok/types/generated-types'

/**
 * Extracts text content from a richtext JSON structure.
 *
 * @param richtext - the richtext JSON structure to extract text from
 */
const extractTextFromRichtext = (richtext: RichtextStoryblok): string => {
  if (!richtext || !richtext.content) return ''

  return richtext.content
    .reduce((text, block) => {
      // Handle paragraph blocks
      if (block.type === 'paragraph' && block.content) {
        const paragraphText = block.content.reduce((acc: string, textBlock: RichtextStoryblok) => {
          // Only include plain text content, skip other types
          if (textBlock.type === 'text') {
            return acc + textBlock.text
          }
          return acc
        }, '')
        return text + paragraphText + ' '
      }
      return text
    }, '')
    .trim()
}

/**
 * Gets excerpt from a blog post, either from manual excerpt or by extracting from content.
 *
 * @param post - the blog post to extract the excerpt from
 * @param maxLength - the maximum length of the excerpt
 */
const getExcerpt = (post: BlogPostPageStoryblok, maxLength = 160): string => {
  // First check if there's a manual excerpt
  if (post.manual_excerpt) {
    return post.manual_excerpt
  }

  // Get text content from blog content blocks
  const contentBlocks = post.blog_content || []
  let extractedText = ''

  for (const block of contentBlocks) {
    // Skip the first text block as it's likely the title
    if (block._uid === contentBlocks[0]._uid) continue

    if (block.component === 'typography' && 'text' in block) {
      extractedText += block.text + ' '
    } else if (block.component === 'typography_copy' && block.text) {
      extractedText += extractTextFromRichtext(block.text) + ' '
    }

    // Check if we have enough text for an excerpt
    if (extractedText.length >= maxLength) {
      break
    }
  }

  // Clean up and truncate
  extractedText = extractedText.trim()
  if (extractedText.length <= maxLength) {
    return extractedText
  }

  // Truncate at the last complete word before EXCERPT_LENGTH
  const truncated = extractedText.substring(0, maxLength)
  const lastSpace = truncated.lastIndexOf(' ')
  return truncated.substring(0, lastSpace) + '...'
}

/**
 * Gets the featured image for a blog post. If no featured image is set, it will try to extract from content by
 * looking for a container with a background image.
 *
 * @param post - the blog post to extract the featured image from
 */
const getFeaturedImage = (post: BlogPostPageStoryblok) => {
  // First check if there's a featured image
  if (post.featured_image) {
    return post.featured_image
  }

  // Otherwise, try to extract from content
  const containerBlok = post.blog_content.find((blok) => blok.component === 'container' && blok.background_image)

  // If there's a container with a background image, use that
  if (containerBlok) {
    return containerBlok.background_image.filename
  }

  return ''
}

export { extractTextFromRichtext, getExcerpt, getFeaturedImage }
